import { type OptionType } from '@hivebrite/design-system/Inputs';

interface QueryFnParams {
  ids?: Array<number>;
  searchText?: string;
}

/**
 * Resolves options from IDs by checking cache and fetching missing ones
 */
export const resolveOptionsFromIds = async <TQueryFnData>(
  ids: Array<number>,
  allAvailableOptions: Array<OptionType>,
  queryFn: (
    params: QueryFnParams,
    signal: AbortSignal,
  ) => Promise<TQueryFnData>,
  responseToOptions: (response: TQueryFnData) => Array<OptionType>,
  addOptionsToCache: (options: Array<OptionType>) => void,
): Promise<Array<OptionType>> => {
  const idsToFetch: Array<number> = [];
  const optionsFromCache = ids
    .map((id) => {
      const option = allAvailableOptions.find(({ value }) => value === id);
      if (!option) {
        idsToFetch.push(id);
      }
      return option;
    })
    .filter(Boolean) as Array<OptionType>;

  // All IDs found in cache
  if (idsToFetch.length === 0) {
    return optionsFromCache;
  }

  const abortController = new AbortController();
  const response = await queryFn({ ids: idsToFetch }, abortController.signal);
  const fetchedOptions = responseToOptions(response);

  addOptionsToCache(fetchedOptions);

  return [...optionsFromCache, ...fetchedOptions];
};
